import React, { useState, useEffect } from 'react';
import EditModalWrapper from './EditModalWrapper';

// Country/State/City data - simplified for common countries
const COUNTRIES = [
    { name: 'India', code: 'IN' },
    { name: 'Germany', code: 'DE' },
    { name: 'United States', code: 'US' },
    { name: 'United Kingdom', code: 'GB' },
    { name: 'United Arab Emirates', code: 'AE' },
    { name: 'Saudi Arabia', code: 'SA' },
    { name: 'Qatar', code: 'QA' },
    { name: 'Kuwait', code: 'KW' },
    { name: 'Oman', code: 'OM' },
    { name: 'Bahrain', code: 'BH' },
    { name: 'Canada', code: 'CA' },
    { name: 'Australia', code: 'AU' },
    { name: 'France', code: 'FR' },
    { name: 'Italy', code: 'IT' },
    { name: 'Spain', code: 'ES' },
    { name: 'Netherlands', code: 'NL' },
    { name: 'Belgium', code: 'BE' },
    { name: 'Switzerland', code: 'CH' },
    { name: 'Austria', code: 'AT' },
];

// Indian states
const INDIAN_STATES = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Puducherry', 'Chandigarh'
];

// German states
const GERMAN_STATES = [
    'Baden-Württemberg', 'Bavaria', 'Berlin', 'Brandenburg', 'Bremen',
    'Hamburg', 'Hesse', 'Lower Saxony', 'Mecklenburg-Vorpommern',
    'North Rhine-Westphalia', 'Rhineland-Palatinate', 'Saarland', 'Saxony',
    'Saxony-Anhalt', 'Schleswig-Holstein', 'Thuringia'
];

// Kerala cities for demo
const KERALA_CITIES = [
    'Thiruvananthapuram', 'Kochi', 'Kozhikode', 'Thrissur', 'Kannur',
    'Kollam', 'Alappuzha', 'Palakkad', 'Malappuram', 'Wayanad', 'Idukki',
    'Kottayam', 'Pathanamthitta', 'Ernakulam', 'Kasaragod'
];

const EditAddressModal = ({ isOpen, onClose, userData, onSave }) => {
    // Helper to parse street address if separate fields are missing
    const getAddressFields = (data) => {
        let street = data?.street_name || data?.address?.street || '';
        let house = data?.house_number || data?.address?.house_number || '';

        // If both are missing, try to parse from street_address
        if (!street && !house && data?.street_address) {
            // Very basic parsing: split by first comma or space if no comma
            const full = data.street_address;
            const commaIndex = full.indexOf(',');
            if (commaIndex !== -1) {
                house = full.substring(0, commaIndex).trim();
                street = full.substring(commaIndex + 1).trim();
            } else {
                // Try splitting by first space (assuming house number first)
                const spaceIndex = full.indexOf(' ');
                if (spaceIndex !== -1) {
                    house = full.substring(0, spaceIndex).trim();
                    street = full.substring(spaceIndex + 1).trim();
                } else {
                    street = full;
                }
            }
        }
        return { street, house };
    };

    const [form, setForm] = useState({
        streetName: '',
        houseNumber: '',
        floorNumber: '',
        country: 'India',
        state: '',
        city: '',
        postalCode: '',
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [availableStates, setAvailableStates] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);

    useEffect(() => {
        if (isOpen) {
            const { street, house } = getAddressFields(userData);
            setForm({
                streetName: street,
                houseNumber: house,
                floorNumber: userData?.floor_number || userData?.address?.floor || '',
                country: userData?.country || userData?.address?.country || 'India',
                state: userData?.state || userData?.address?.state || '',
                city: userData?.city || userData?.address?.city || '',
                postalCode: userData?.postal_code || userData?.address?.postal_code || '',
            });
        }
    }, [isOpen, userData]);

    // Update states when country changes
    useEffect(() => {
        if (form.country === 'India') {
            setAvailableStates(INDIAN_STATES);
        } else if (form.country === 'Germany') {
            setAvailableStates(GERMAN_STATES);
        } else {
            setAvailableStates([]);
        }
    }, [form.country]);

    // Update cities when state changes (simplified for Kerala only)
    useEffect(() => {
        if (form.state === 'Kerala') {
            setAvailableCities(KERALA_CITIES);
        } else {
            setAvailableCities([]);
        }
    }, [form.state]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validation
        if (!form.country) {
            alert('Country is required');
            return;
        }

        setIsSubmitting(true);
        try {
            const formData = new FormData();
            formData.append('street_name', form.streetName);
            formData.append('house_number', form.houseNumber);
            formData.append('floor_number', form.floorNumber);
            formData.append('country', form.country);
            formData.append('state', form.state);
            formData.append('city', form.city);
            formData.append('postal_code', form.postalCode);

            // Also create a combined address string for backward compatibility
            const addressParts = [
                form.houseNumber,
                form.streetName,
                form.city,
                form.state,
                form.country,
                form.postalCode
            ].filter(Boolean);
            formData.append('address', addressParts.join(', '));

            await onSave(formData);
            onClose();
        } catch (error) {
            console.error('Error saving address:', error);
            alert('Failed to save changes. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <EditModalWrapper
            isOpen={isOpen}
            onClose={onClose}
            title="Edit Address"
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            size="md"
        >
            <div className="space-y-5">
                {/* Street & House Number */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Street Name
                        </label>
                        <input
                            type="text"
                            value={form.streetName}
                            onChange={(e) => setForm({ ...form, streetName: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="Enter street name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            House Number
                        </label>
                        <input
                            type="text"
                            value={form.houseNumber}
                            onChange={(e) => setForm({ ...form, houseNumber: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="e.g., c143"
                        />
                    </div>
                </div>

                {/* Floor Number & Country */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Floor Number
                        </label>
                        <input
                            type="text"
                            value={form.floorNumber}
                            onChange={(e) => setForm({ ...form, floorNumber: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="Enter floor name"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Country <span className="text-red-500">*</span>
                        </label>
                        <select
                            value={form.country}
                            onChange={(e) => setForm({ ...form, country: e.target.value, state: '', city: '' })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                            required
                        >
                            <option value="">Select country</option>
                            {COUNTRIES.map((c) => (
                                <option key={c.code} value={c.name}>{c.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {/* State & City */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            State <span className="text-red-500">*</span>
                        </label>
                        {availableStates.length > 0 ? (
                            <select
                                value={form.state}
                                onChange={(e) => setForm({ ...form, state: e.target.value, city: '' })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                            >
                                <option value="">Select state</option>
                                {availableStates.map((s) => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={form.state}
                                onChange={(e) => setForm({ ...form, state: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                                placeholder="Enter state"
                            />
                        )}
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            City <span className="text-red-500">*</span>
                        </label>
                        {availableCities.length > 0 ? (
                            <select
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all bg-white text-slate-900"
                            >
                                <option value="">Select city</option>
                                {availableCities.map((c) => (
                                    <option key={c} value={c}>{c}</option>
                                ))}
                            </select>
                        ) : (
                            <input
                                type="text"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                                placeholder="Enter city"
                            />
                        )}
                    </div>
                </div>

                {/* Postal Code */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                            Postal Code
                        </label>
                        <input
                            type="text"
                            value={form.postalCode}
                            onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                            className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all text-slate-900"
                            placeholder="Enter postal code"
                        />
                    </div>
                </div>
            </div>
        </EditModalWrapper>
    );
};

export default EditAddressModal;
